/**
 * Infralytics analytics client — feeds the in-house tracker at /api/analytics/event.
 *
 * Privacy:
 *   • Sends NOTHING until the user opts in via the consent banner
 *     (localStorage key `infx_consent === "granted"`).
 *   • Persists session_id in localStorage for cross-tab correlation.
 *   • Uses `navigator.sendBeacon` on `pagehide` so the final pageview duration
 *     reaches the server reliably (works around the unload-blocking fetch issue).
 */

const STORAGE_KEY_SESSION = "infx_session_id";
const STORAGE_KEY_CONSENT = "infx_consent";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const safeGet = (k) => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const safeSet = (k, v) => {
  try { localStorage.setItem(k, v); } catch { /* ignore */ }
};

const newSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const getSessionId = () => {
  let sid = safeGet(STORAGE_KEY_SESSION);
  if (!sid) {
    sid = newSessionId();
    safeSet(STORAGE_KEY_SESSION, sid);
  }
  return sid;
};

export const hasConsent = () => safeGet(STORAGE_KEY_CONSENT) === "granted";
export const setConsent = (v) => safeSet(STORAGE_KEY_CONSENT, v);
export const consentStatus = () => safeGet(STORAGE_KEY_CONSENT);  // "granted" | "denied" | null

const send = (body) => {
  if (!hasConsent()) return;
  if (!BASE) return;
  const url = `${BASE}/api/analytics/event`;
  const payload = JSON.stringify(body);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch { /* fall through to fetch */ }
  // Fallback (also runs when sendBeacon refuses non-CORS-safelisted MIME)
  try {
    fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Infx-Session": body.session_id },
      body: payload,
      keepalive: true,
    }).catch(() => { /* swallow — never block UX */ });
  } catch { /* ignore */ }
};

// ── Pageview tracking with duration ─────────────────────────────────────────
let currentPath = null;
let currentEnteredAt = null;

const flushCurrentPageview = () => {
  if (!currentPath || !currentEnteredAt) return;
  const duration_ms = Date.now() - currentEnteredAt;
  send({
    session_id: getSessionId(),
    event_type: "pageview",
    path: currentPath,
    referrer: document.referrer || "",
    duration_ms,
    timestamp_client: new Date(currentEnteredAt).toISOString(),
  });
  currentPath = null;
  currentEnteredAt = null;
};

export const trackPageview = (path) => {
  if (!hasConsent()) return;
  // Flush the previous page so its duration_ms is captured
  if (currentPath && currentPath !== path) {
    flushCurrentPageview();
  }
  currentPath = path;
  currentEnteredAt = Date.now();
};

// Flush on unload / visibility change
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushCurrentPageview);
  window.addEventListener("beforeunload", flushCurrentPageview);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushCurrentPageview();
  });

  // ── Global JS error capture ────────────────────────────────────────────────
  const errBudget = { count: 0, max: 10 };  // rate-limit: max 10 errors per session
  const captureError = (detail, path) => {
    if (errBudget.count++ >= errBudget.max) return;
    send({
      session_id: getSessionId(),
      event_type: "error",
      path: path || (typeof window !== "undefined" ? window.location.pathname : ""),
      error_detail: String(detail).slice(0, 480),
      metadata: { source: "js" },
    });
  };
  window.addEventListener("error", (e) => {
    captureError(`${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`);
  });
  window.addEventListener("unhandledrejection", (e) => {
    captureError(`UnhandledRejection: ${e.reason?.message || e.reason || "unknown"}`);
  });
}

// ── Named-action tracking (AI query, PDF gen, compare run, signup, checkout) ─
export const trackAction = (action, metadata = {}) => {
  if (!hasConsent()) return;
  send({
    session_id: getSessionId(),
    event_type: "action",
    path: typeof window !== "undefined" ? window.location.pathname : "",
    metadata: { action, ...metadata },
  });
};

// ── Manual error tracking (caught exceptions the UI handled) ───────────────
export const trackError = (detail, metadata = {}) => {
  if (!hasConsent()) return;
  send({
    session_id: getSessionId(),
    event_type: "error",
    path: typeof window !== "undefined" ? window.location.pathname : "",
    error_detail: String(detail).slice(0, 480),
    metadata: { source: "ui", ...metadata },
  });
};
